import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

/**
 * One side-menu item. `icon` is the design system icon-font class.
 */
interface MenuItem {
  /**
   * Route the item navigates to.
   */
  route: string;

  /**
   * Text rendered next to the icon, and the item's accessible name.
   */
  label: string;

  /**
   * Design system icon-font class.
   */
  icon: string;
}

/**
 * Shell for the authenticated screens: fixed side menu plus a content area with
 * the page title.
 *
 * ## Why it exists
 *
 * Until it landed there were only loose routes — getting from the dashboard to
 * the reports meant typing the URL. And no screen showed its own title, so the
 * user had no way of knowing where they were.
 *
 * ## The active item is red on purpose
 *
 * The Samba navigation spec is explicit about this: *"teal #23779A on horizontal
 * components (Tabs, Segmented, Pagination); **brand #EC0000 on the side menu
 * (Sidebar)**"*. That is, the active-state colour depends on the component's
 * orientation — the `/relatorios` tabs are teal and the menu item is red. The
 * tokens used are the ones the spec itself names
 * (`--color-border-active-primary` and `--color-icon-active-primary`), not loose
 * hex values.
 *
 * ## The title comes from the route
 *
 * Each route declares `data.pageTitle` and the shell renders it. That keeps every
 * page from repeating the header markup and keeps the title in the same place on
 * all of them — and it is also where `document.title` comes from.
 */
@Component({
  selector: 'shell',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent {
  /**
   * Router, watched for navigation and read for the active route's title.
   */
  private readonly router = inject(Router);

  /**
   * Side-menu items, in the order they are rendered.
   */
  public readonly items: MenuItem[] = [
    { route: '/dashboard', label: 'Visão geral', icon: 'cds-icon-outlined-home' },
    { route: '/extrato', label: 'Extrato Inteligente', icon: 'cds-icon-outlined-list' },
    { route: '/relatorios', label: 'Relatórios', icon: 'cds-icon-outlined-chart-bar' },
    { route: '/design-system', label: 'Design system', icon: 'cds-icon-outlined-palette' },
  ];

  /**
   * Title of the active route, read from `data.pageTitle`.
   *
   * `startWith` emits the initial value: `NavigationEnd` only fires on subsequent
   * navigations, so without it the first screen would open with no title.
   *
   * The filter is `instanceof`, not a `constructor.name` comparison: the
   * production build minifies class names, so comparing by name works in dev and
   * fails silently in production — the title used to freeze on the first route's.
   */
  public readonly pageTitle = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.currentTitle()),
    ),
    { initialValue: '' },
  );

  /**
   * Walks the active route tree and returns the deepest declared `pageTitle`.
   *
   * @returns the title of the active route, or an empty string when none declares one
   */
  private currentTitle(): string {
    let route = this.router.routerState.snapshot.root;
    let title = '';

    while (route) {
      title = (route.data?.['pageTitle'] as string) ?? title;
      route = route.firstChild as typeof route;
    }

    return title;
  }
}
